import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Auth } from 'aws-amplify';
import {
  Box,
  ButtonBase,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  Business as BusinessIcon,
  ExitToApp as ExitToAppIcon,
  VpnLock as VpnLockIcon,
} from '@material-ui/icons';

import { PINPEO } from 'theme/palette';
import { hasPermissions } from 'utils/permissions';

import AccountModal from '../TopBar/AccountModal';

const useStyles = makeStyles((theme) => ({
  root: {
    borderTop: '1px solid rgba(255,255,255,.1)',
    padding: '12px 14px',
    flexShrink: 0,
  },
  button: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    borderRadius: 9,
    padding: '6px 4px',
    textAlign: 'left',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,.08)',
    },
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: theme.brand.primary,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    flexShrink: 0,
  },
  name: {
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  email: {
    color: 'rgba(255,255,255,.5)',
    fontSize: 11.5,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const getInitials = (label = '') =>
  String(label)
    .trim()
    .split(/\s+/)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'U';

/**
 * Sección de cuenta al pie del sidebar (rediseño). Conserva el menú
 * Compañía / Cuenta / Cerrar sesión y el AccountModal existentes.
 */
function AccountSection({ collapsed }) {
  const classes = useStyles();
  const history = useHistory();
  const ref = useRef(null);

  const [isOpen, setOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState();
  const [modal, setModal] = useState();

  const handleLogout = async function () {
    try {
      await Auth.signOut({ global: true });
    } catch (error) {
      console.debug(error.message);
    } finally {
      window.location.replace('/auth/login');
    }
  };

  const handleModalAccount = async function () {
    const user = await Auth.currentAuthenticatedUser();

    setModal({
      data: {
        frmSetPreferredMFA: {
          mfa: {
            SMS_MFA: 'SMS',
            SOFTWARE_TOKEN_MFA: 'TOTP',
          }[user?.preferredMFA] || 'NOMFA',
          phone_number: user?.attributes?.phone_number,
        },
        frmUserToken: user,
      },
      events: {
        onClose: () => setModal(),
      },
      open: true,
    });
  };

  useEffect(() => {
    const handleInit = async function () {
      try {
        const user = await Auth.currentAuthenticatedUser();

        setCurrentUser({
          label: [
            user?.attributes?.name || '',
            user?.attributes?.family_name || '',
          ].filter((item) => !!item).join(' ')
            || user?.attributes?.email
            || user?.attributes?.phone_number
            || user?.username
            || 'Usuario',
          email: user?.attributes?.email || '',
        });
      } catch (error) {
        console.debug(error?.message);
      }
    };

    handleInit();
  }, []);

  const menuOptions = [
    {
      hidden: !hasPermissions('company.select.getCompany') || process.env.NODE_ENV === 'production',
      icon: <BusinessIcon fontSize="small" />,
      label: 'Compañia',
      fn: function () {
        history.push('/company');
      },
    },
    {
      icon: <VpnLockIcon fontSize="small" />,
      label: 'Cuenta',
      fn: handleModalAccount,
    },
    {
      type: 'divider',
    },
    {
      icon: <ExitToAppIcon fontSize="small" />,
      label: 'Cerrar sesión',
      fn: handleLogout,
    },
  ];

  return (
    <Box className={classes.root} style={collapsed ? { padding: '12px 10px' } : undefined}>
      <AccountModal {...modal} />

      <Tooltip title={collapsed ? (currentUser?.label || 'Cuenta') : ''} placement="right">
        <ButtonBase
          className={classes.button}
          style={collapsed ? { justifyContent: 'center' } : undefined}
          onClick={() => setOpen(true)}
          ref={ref}
        >
          <Box className={classes.avatar}>{getInitials(currentUser?.label)}</Box>

          {!collapsed && (
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Typography className={classes.name}>
                {currentUser?.label || 'Usuario'}
              </Typography>
              {currentUser?.email && (
                <Typography className={classes.email}>
                  {currentUser.email}
                </Typography>
              )}
            </Box>
          )}

          {!collapsed && (
            <ExitToAppIcon
              style={{ fontSize: 17, color: 'rgba(255,255,255,.5)', flexShrink: 0 }}
            />
          )}
        </ButtonBase>
      </Tooltip>

      <Menu
        onClose={() => setOpen(false)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: collapsed ? 'right' : 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: collapsed ? 'left' : 'center',
        }}
        keepMounted
        PaperProps={{
          style: {
            width: 220,
            borderRadius: 12,
            border: `1px solid ${PINPEO.border}`,
            boxShadow: PINPEO.shadowPopover,
          },
        }}
        getContentAnchorEl={null}
        anchorEl={ref.current}
        open={isOpen}
      >
        {menuOptions.filter((item) => item.hidden !== true).map((item, index) => {
          if (item.type === 'divider') {
            return (<Divider key={index} />);
          }

          return (
            <MenuItem
              key={index}
              onClick={() => {
                setOpen(false);

                if (typeof item.fn === 'function') {
                  item.fn();
                }
              }}
            >
              <ListItemIcon children={item.icon} /> {item.label}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}

AccountSection.propTypes = {
  collapsed: PropTypes.bool,
};

AccountSection.defaultProps = {
  collapsed: false,
};

export default AccountSection;
